// src/pages/BusinessDetailPage.tsx
//
// Tela do negócio — réplica literal do wireframe canônico
// docs/RAIOS-UX-SIDEBAR-SPEC.html, aba "04 Negócios" (correção UX v2, CW4):
// container .bizdetail com header .bizdetail-h (logo lg + upload + nome lg +
// tipo + status), abas .biztabs (Visão geral · Repositórios · Demandas ·
// Configurações — SEM C-Levels, decisão Breno) e corpo .bizbody.
//
// C4: contagens da Visão geral vêm do payload (repos_count/demands_count).
// C6: fetches independentes em paralelo (business + dna, sem cascata);
// abas Repositórios e Demandas carregam LAZY (fetch dentro do tab, que só
// monta quando a aba abre).

import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api, type Business, type BusinessDNA } from '../lib/api';
import { Skeleton } from '@/components/ui/Skeleton';
import { BusinessLogo, BusinessLogoUploadButton } from '@/components/business/BusinessLogo';
import { BusinessOverviewTab } from '@/components/business/BusinessOverviewTab';
import { BusinessReposTab } from '@/components/business/BusinessReposTab';
import { BusinessDemandsTab } from '@/components/business/BusinessDemandsTab';
import { BusinessMarketingTab } from '@/components/business/BusinessMarketingTab';
import { BusinessSettingsPanel } from '@/components/business/BusinessSettingsPanel';
import { LIFECYCLE_LABELS } from '@/components/business/business-constants';
import { ChevronLeft } from 'lucide-react';
import '@/styles/wf-biz.css';

type Tab = 'overview' | 'repos' | 'demands' | 'marketing' | 'settings';

const TAB_ITEMS: { value: Tab; label: string }[] = [
  { value: 'overview', label: 'Visão geral' },
  { value: 'repos', label: 'Repositórios' },
  { value: 'demands', label: 'Demandas' },
  { value: 'marketing', label: 'Marketing' },
  { value: 'settings', label: 'Configurações' },
];

export function BusinessDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [business, setBusiness] = useState<Business | null>(null);
  const [dna, setDNA] = useState<BusinessDNA | null>(null);
  const [activeTab, setActiveTab] = useState<Tab>('overview');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [logoError, setLogoError] = useState('');

  useEffect(() => {
    if (!id) return;
    let alive = true;
    // Microtask: evita setState síncrono no corpo do effect (lint).
    Promise.resolve().then(() => { if (alive) setLoading(true); });

    // C6: fetches INDEPENDENTES em paralelo — o header não espera o DNA.
    api.getBusiness(id)
      .then((biz) => { if (alive) setBusiness(biz); })
      .catch((err) => { if (alive) setError(err.message); })
      .finally(() => { if (alive) setLoading(false); });
    api.getBusinessDNA(id)
      .then((d) => { if (alive) setDNA(d); })
      .catch(() => undefined);

    return () => { alive = false; };
  }, [id]);

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-16 w-72 rounded-xl" />
        <Skeleton className="h-10 w-96 rounded-lg" />
        <Skeleton className="h-64 rounded-xl" />
      </div>
    );
  }

  if (error || !business) {
    return (
      <div className="wf-card text-center">
        <p style={{ color: 'var(--wf-danger-text)', fontWeight: 600, marginBottom: 4 }}>
          Erro ao carregar negócio
        </p>
        <p className="text-sm" style={{ color: 'var(--wf-muted)' }}>
          {error || 'Negócio não encontrado'}
        </p>
        <Link
          to="/businesses"
          className="inline-block mt-4 text-sm"
          style={{ color: 'var(--wf-accent-text)' }}
        >
          Voltar para lista
        </Link>
      </div>
    );
  }

  return (
    <div>
      {/* Breadcrumb */}
      <Link
        to="/businesses"
        className="inline-flex items-center gap-1.5 text-xs transition-colors"
        style={{ color: 'var(--wf-muted)' }}
      >
        <ChevronLeft className="w-3.5 h-3.5" aria-hidden="true" />
        Negócios
      </Link>

      {logoError && (
        <p className="text-xs mt-2" style={{ color: 'var(--wf-danger-text)' }}>{logoError}</p>
      )}

      {/* Tela do negócio (wireframe .bizdetail) */}
      <div className="wf-bizdetail">
        {/* Header: logo lg + upload + nome lg + tipo + status (.bizdetail-h) */}
        <div className="wf-bizdetail-h">
          <div style={{ position: 'relative', flex: '0 0 auto' }}>
            <BusinessLogo business={business} size="lg" />
            <BusinessLogoUploadButton
              business={business}
              onUpdated={setBusiness}
              onError={setLogoError}
              className="absolute -bottom-2 -right-2"
            />
          </div>
          <div>
            <div className="wf-bizname lg">{business.name}</div>
            <div className="wf-bizmeta">
              <span className="wf-biztype">{business.subtype || business.type}</span>
              <span className={`wf-bizst ${business.lifecycle_status}`}>
                {LIFECYCLE_LABELS[business.lifecycle_status] ?? business.lifecycle_status}
              </span>
            </div>
          </div>
        </div>

        {/* Abas (wireframe .biztabs) — sem C-Levels */}
        <div className="wf-biztabs" role="tablist">
          {TAB_ITEMS.map((t) => (
            <button
              key={t.value}
              type="button"
              role="tab"
              aria-selected={activeTab === t.value}
              className={activeTab === t.value ? 'active' : undefined}
              onClick={() => setActiveTab(t.value)}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Corpo (wireframe .bizbody) — abas lazy: o conteúdo só monta (e só
            busca) quando a aba abre (C6). */}
        <div className="wf-bizbody">
          {activeTab === 'overview' && (
            <BusinessOverviewTab business={business} dna={dna} />
          )}

          {activeTab === 'repos' && <BusinessReposTab businessId={business.id} />}

          {activeTab === 'demands' && <BusinessDemandsTab businessId={business.id} />}

          {activeTab === 'marketing' && <BusinessMarketingTab businessId={business.id} />}

          {activeTab === 'settings' && (
            <BusinessSettingsPanel
              business={business}
              dna={dna}
              onBusinessUpdated={setBusiness}
              onDnaUpdated={setDNA}
            />
          )}
        </div>
      </div>
    </div>
  );
}
